import * as React from "react";

import {
  Alert,
  Box,
  Button,
  Center,
  Container,
  Flex,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconChecklist, IconDownload, IconStarFilled } from "@tabler/icons-react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";

import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useBackendClient } from "../../hooks/useBackendClient";
import { useCountEvent } from "../../hooks/useCountEvent";
import { useDownloadPDF } from "../../hooks/useDownloadPDF";
import { SerializedChecklistDocument } from "../../lib/model/checklistSerialized";
import { librarySlice } from "../../lib/state/reducer";
import { HardPDFPreview } from "../renderer/pdf/PDFPreview";

export const ShareImportPage = (): JSX.Element | null => {
  const params = useParams();
  const navigate = useNavigate();

  if (!params.shareId) {
    navigate("/");
    return null;
  }
  const shareId = params.shareId;

  const [documentContents, setDocumentContents] =
    React.useState<SerializedChecklistDocument | null>(null);
  const [fetchError, setFetchError] = React.useState<string | null>(null);

  const { GET } = useBackendClient();

  React.useEffect(() => {
    setFetchError(null);
    setDocumentContents(null);

    GET(`/shared/{documentId}`, {
      params: {
        path: {
          documentId: shareId,
        },
      },
    })
      .then(({ data, error, response: _response }) => {
        // TODO: error handling
        if (error) {
          if (error.message) {
            // TODO: report error to bugsnag
            setFetchError(error.message);
          } else {
            // TODO: report error to bugsnag
            setFetchError("An unknown error occurred. Please try again.");
          }
        } else if (!data) {
          setFetchError("An unknown error occurred. Please try again.");
        } else {
          const d = data as SerializedChecklistDocument;
          setDocumentContents(d);
        }
      })
      .catch((_error) => {
        // TODO: report error to bugsnag
        setFetchError("An unknown error occurred. Please try again.");
      });
  }, [shareId, setDocumentContents, setFetchError, GET]);

  const loading = !documentContents && !fetchError;

  return (
    <Container size="md" pt="xl" style={{ height: "100%" }}>
      {documentContents?.title && (
        <Helmet>
          <title>{documentContents.title}</title>
        </Helmet>
      )}
      {!documentContents ? (
        <Center style={{ height: "100%" }}>
          <LoadingOrError
            loading={loading}
            error={fetchError ?? "Something went wrong when loading the document."}
          />
        </Center>
      ) : (
        <SharedDocumentView document={documentContents} shareId={shareId} />
      )}
    </Container>
  );
};

const SharedDocumentView = ({
  document,
  shareId,
}: {
  document: SerializedChecklistDocument;
  shareId: string;
}): JSX.Element => {
  const { download: downloadPdf, isLoading: isDownloading } = useDownloadPDF();
  const reduxDispatch = useAppDispatch();
  const navigate = useNavigate();
  const countEvent = useCountEvent();

  return (
    <Flex direction="column" style={{ height: "100%" }}>
      <Stack style={{ flex: "0 0 auto" }} align="center" spacing="md">
        <Stack spacing={0} align="center">
          <Title order={1}>{document.title}</Title>
          <Text fs="italic" c="dimmed" fz="sm">
            {shareId}
          </Text>
        </Stack>
        <Group>
          <Button
            variant="gradient"
            gradient={{ from: "cyan.4", to: "blue", deg: 45 }}
            leftIcon={<IconChecklist size={20} />}
            onClick={() => {
              countEvent("customize-from-shared", shareId);
              reduxDispatch(librarySlice.actions.newDocumentFromSerialized(document));
              navigate("/editor");
            }}
          >
            Customize...
          </Button>
          <Button
            variant="gradient"
            gradient={{ from: "orange", to: "red", deg: 45 }}
            leftIcon={<IconDownload size={20} />}
            onClick={() => {
              countEvent("download-from-shared", shareId);
              downloadPdf(document);
            }}
            loading={isDownloading}
          >
            Download PDF
          </Button>
          <Button
            variant="gradient"
            gradient={{ from: "grape.6", to: "pink.6", deg: 45 }}
            leftIcon={<IconStarFilled size={18} />}
            onClick={() => {
              countEvent("add-to-library-from-shared", shareId);
              reduxDispatch(librarySlice.actions.newDocumentFromSerialized(document));
              navigate("/manage");
            }}
          >
            Add to My Checklists
          </Button>
        </Group>
      </Stack>
      <Box mt="xl" style={{ flex: "1 1 auto" }}>
        <HardPDFPreview document={document} />
      </Box>
    </Flex>
  );
};

const LoadingOrError = ({
  loading,
  error,
}: {
  loading: boolean | null;
  error: string | null;
}): JSX.Element | null => {
  if (loading) {
    return <Loader size="md" />;
  } else if (error !== null) {
    return (
      <Alert title="Error" color="yellow">
        {error}
      </Alert>
    );
  } else {
    return null;
  }
};
